import { auth } from "../firebase";

const API_BASE_URL =
  process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";

export async function getBearerToken() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("You must be signed in to use tenant admin APIs.");
  }
  // Use the cached token (no force-refresh). Firebase refreshes automatically
  // when it is within 5 minutes of expiry. Force-refreshing on every call
  // triggers Firebase's rate limiter ("too many requests / temporarily blocked").
  try {
    return await user.getIdToken(false);
  } catch {
    // If the cached token fetch fails, try a single forced refresh as fallback.
    return await user.getIdToken(true);
  }
}

function buildUrl(path, query = {}) {
  const url = new URL(`${API_BASE_URL}${path}`);
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}`.trim() !== "") {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}

async function request(path, { method = "GET", query, body, timeoutMs, timeoutMessage } = {}) {
  // Attempt to resolve a bearer token. If it fails and demo-mode is enabled
  // via the URL flag, surface a synthetic 401 so client widgets can show demo data.
  let token;
  try {
    token = await getBearerToken();
  } catch (err) {
    const demo = typeof window !== "undefined" && (window.__LEDGERHUB_DEMO || new URLSearchParams(window.location.search).get("demo") === "true");
    if (demo) {
      const e = new Error("Demo mode - no auth");
      e.status = 401;
      throw e;
    }
    throw err;
  }
  const url = buildUrl(path, query);
  const urlString = url.toString();

  // Log URL to aid debugging when network errors occur in the browser console
  // (will be visible in the devtools Console tab)
  // eslint-disable-next-line no-console
  console.debug(`[tenantUserAdminService] fetch ${method} ${urlString}`);

  let response;
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timeoutId = timeoutMs && controller
    ? window.setTimeout(() => controller.abort(), timeoutMs)
    : null;
  try {
        const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    response = await fetch(urlString, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller?.signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error(timeoutMessage || "The request timed out. Please try again.");
    }
    // Network-level errors (CORS, connection refused, DNS, etc.) manifest as a
    // TypeError: Failed to fetch in browsers. Surface a clearer message so the
    // UI can show a helpful error and we get a useful console log to debug.
    // eslint-disable-next-line no-console
    console.error(`[tenantUserAdminService] network error fetching ${url}:`, err);
    throw new Error(`Network error when fetching ${url}: ${err?.message || err}`);
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Log API-level errors separately so they aren't confused with network errors.
    // eslint-disable-next-line no-console
    console.error(`[tenantUserAdminService] API error ${response.status} ${url}:`, payload);
    const httpErr = new Error(payload?.error || `Request failed with status ${response.status}`);
    httpErr.status = response.status;
    httpErr.payload = payload;
    throw httpErr;
  }

  return payload;
}

/*Bearer ${token}` Exported alias so other modules can make arbitrary authenticated API calls. */
export async function apiRequest(path, { method = "GET", query, body } = {}) {
  return request(path, { method, query, body: typeof body === "string" ? JSON.parse(body) : body });
}

export async function requestBankConsent({ clientName, clientEmail, bank, country, tenantId, clientProfileId }) {
  return request("/consent-requests", {
    method: "POST",
    body: { clientName, clientEmail, bank, country, tenantId, clientProfileId },
  });
}

// ── Client Profiles ───────────────────────────────────────────────────────────

export async function getClientProfile(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}`,
    { method: "GET" }
  );
}

export async function generateScimToken(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/sso/generate-scim-token`, {
    method: "POST",
  });
}

export async function listClientProfiles({ tenantId, status } = {}) {
  const data = await request(`/tenant/${encodeURIComponent(tenantId)}/client-profiles`, {
    method: "GET",
    query: status ? { status } : {},
  });
  return { clientProfiles: Array.isArray(data?.clientProfiles) ? data.clientProfiles : [] };
}

export async function createClientProfile(tenantId, payload) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/client-profiles`, {
    method: "POST",
    body: payload,
  });
}

export async function updateClientProfile(tenantId, clientId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}`,
    { method: "PATCH", body: payload }
  );
}

export async function archiveClientProfile(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}`,
    { method: "DELETE" }
  );
}

export async function deleteClientProfilePermanent(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/permanent`,
    { method: "DELETE" }
  );
}

// ── Individual Clients (type-filtered views over the shared clientProfiles collection) ──

export async function listIndividualClients({ tenantId, status } = {}) {
  const data = await request(`/tenant/${encodeURIComponent(tenantId)}/client-profiles`, {
    method: "GET",
    query: { clientType: "individual", ...(status ? { status } : {}) },
  });
  return { clients: Array.isArray(data?.clientProfiles) ? data.clientProfiles : [] };
}

export async function getIndividualClient(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}`,
    { method: "GET" }
  );
}

export async function createIndividualClient(tenantId, payload) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/client-profiles`, {
    method: "POST",
    body: { ...payload, clientType: "individual" },
  });
}

export async function updateIndividualClient(tenantId, clientId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}`,
    { method: "PATCH", body: payload }
  );
}

export async function archiveIndividualClient(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}`,
    { method: "DELETE" }
  );
}

export async function provisionDirectorProfiles(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/provision-director-profiles`, {
    method: "POST",
  });
}

/*Bearer ${token}`
 * Upload an ID document image and extract personal details using Gemini Vision.
 * Returns { extracted: { fullLegalName, dateOfBirth, documentType, ... } }
 */
export async function extractIndividualIdDocument(tenantId, file) {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const token = await getBearerToken();

  const formData = new FormData();
  formData.append("idDocument", file);

  const response = await fetch(
    `${process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1"}/tenant/${encodeURIComponent(tenantId)}/individual-clients/extract-id`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    }
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Failed with status ${response.status}`);
  return payload;
}

/*Bearer ${token}`
 * Upload a passport image for a director. Extracts data via Gemini Vision,
 * compares against stored director fields, and saves results.
 * Returns { extracted, discrepancies, passportUrl }
 */
export async function uploadDirectorPassport(tenantId, clientId, directorId, file) {
  const token = await getBearerToken();
  const formData = new FormData();
  formData.append("passport", file);

  const response = await fetch(
    `${process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1"}/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/directors/${encodeURIComponent(directorId)}/passport`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    }
  );

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Failed with status ${response.status}`);
  return payload;
}

// ─── Master services & client service assignments ─────────────────────────────

export async function getVatDraft(tenantId, clientId, period) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(period)}`,
    { method: "GET" }
  );
}

export async function saveManualVatDraft(tenantId, clientId, period, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(period)}/manual`,
    { method: "PUT", body: payload }
  );
}

export async function getAiUsage(tenantId, { start, end, limit } = {}) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/ai-usage`, {
    method: "GET",
    query: { start, end, limit },
  });
}

export async function getAdminAiUsage({ tenantId, start, end, limit } = {}) {
  return request(`/admin/ai-usage`, {
    method: "GET",
    query: { tenantId, start, end, limit },
  });
}

export async function submitVatDraft(tenantId, clientId, period) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(period)}/submit`,
    { method: "POST" }
  );
}

export async function submitManualVatDraft(tenantId, clientId, period) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(period)}/manual-submit`,
    { method: "POST" }
  );
}

export async function listMasterServices() {
  return request("/services/master");
}

export async function listClientServices(tenantId, clientId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/services`);
}

export async function assignClientService(tenantId, clientId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/services`,
    { method: "POST", body: payload }
  );
}

export async function updateClientService(tenantId, clientId, assignmentId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/services/${encodeURIComponent(assignmentId)}`,
    { method: "PATCH", body: payload }
  );
}

export async function removeClientService(tenantId, clientId, assignmentId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/services/${encodeURIComponent(assignmentId)}`,
    { method: "DELETE" }
  );
}

// ─── Jobs & tasks ─────────────────────────────────────────────────────────────

export async function listClientJobs(tenantId, clientId, { status } = {}) {
  // clientId=null → tenant-wide jobs board
  if (!clientId) {
    return request(
      `/tenant/${encodeURIComponent(tenantId)}/jobs`,
      { query: { status } }
    );
  }
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/jobs`,
    { query: { status } }
  );
}

export async function createClientJob(tenantId, clientId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/jobs`,
    { method: "POST", body: payload }
  );
}

export async function updateClientJob(tenantId, jobId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/jobs/${encodeURIComponent(jobId)}`,
    { method: "PATCH", body: payload }
  );
}

export async function listJobTasks(tenantId, jobId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/jobs/${encodeURIComponent(jobId)}/tasks`);
}

export async function updateJobTask(tenantId, jobId, taskId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/jobs/${encodeURIComponent(jobId)}/tasks/${encodeURIComponent(taskId)}`,
    { method: "PATCH", body: payload }
  );
}

export async function deleteClientJob(tenantId, jobId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/jobs/${encodeURIComponent(jobId)}`,
    { method: "DELETE" }
  );
}

export async function lookupCompaniesHouse(companyNumber) {
  return request(`/companies-house/lookup/${encodeURIComponent(companyNumber)}`, { method: "GET" });
}

export async function createClientProfileFromCH(tenantId, companyNumber, extraFields = {}) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/client-profiles/from-companies-house`, {
    method: "POST",
    body: { companyNumber, ...extraFields },
  });
}

export async function resyncClientFromCH(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/resync`,
    { method: "POST" }
  );
}

export async function getClientChangeLog(tenantId, clientId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/change-log`,
    { method: "GET" }
  );
  return { changeLog: Array.isArray(data?.changeLog) ? data.changeLog : [] };
}

export async function listNotifications(tenantId, { unreadOnly = false } = {}) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/notifications`,
    { method: "GET", query: unreadOnly ? { unreadOnly: "true" } : {} }
  );
  return {
    notifications: Array.isArray(data?.notifications) ? data.notifications : [],
    summary: data?.summary || {},
  };
}

export async function markNotificationRead(tenantId, notificationId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/notifications/${encodeURIComponent(notificationId)}/read`,
    { method: "PATCH" }
  );
}

export async function listDirectors(tenantId, clientId, { activeOnly = false } = {}) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/directors`,
    { method: "GET", query: activeOnly ? { activeOnly: "true" } : {} }
  );
  return { directors: Array.isArray(data?.directors) ? data.directors : [] };
}

export async function listAllDirectorsForTenant(tenantId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/directors`,
    { method: "GET" }
  );
  return { directors: Array.isArray(data?.directors) ? data.directors : [] };
}

export async function createDirector(tenantId, clientId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/directors`,
    { method: "POST", body: payload }
  );
}

export async function updateDirector(tenantId, clientId, directorId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/directors/${encodeURIComponent(directorId)}`,
    { method: "PATCH", body: payload }
  );
}

export async function updateDirectorVerification(tenantId, clientId, directorId, state, notes) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/directors/${encodeURIComponent(directorId)}/verify`,
    { method: "PATCH", body: { state, notes } }
  );
}

export async function getDirectorHistory(tenantId, clientId, directorId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/directors/${encodeURIComponent(directorId)}/history`,
    { method: "GET" }
  );
  return { events: Array.isArray(data?.events) ? data.events : [] };
}


export async function listClientProfileConsents(tenantId, clientId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/consents`,
    { method: "GET" }
  );
  return { consents: Array.isArray(data?.consents) ? data.consents : [] };
}

// ── AI Record Requests ────────────────────────────────────────────────────────

export async function previewRecordRequest(tenantId, clientId, options = {}) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/record-requests/preview`,
    { method: "POST", body: options }
  );
}

export async function sendRecordRequest(tenantId, clientId, options = {}) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/record-requests`,
    { method: "POST", body: options }
  );
}

export async function listRecordRequests(tenantId, clientId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/record-requests`,
    { method: "GET" }
  );
  return { recordRequests: Array.isArray(data?.recordRequests) ? data.recordRequests : [] };
}

export async function listClientInbox(tenantId, clientId, limit = 50) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/inbox?limit=${limit}`,
    { method: "GET" }
  );
  return { emails: Array.isArray(data?.emails) ? data.emails : [] };
}

export async function listTenantInbox(tenantId, { limit = 200, direction, status } = {}) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/inbox`,
    { method: "GET", query: { limit, ...(direction ? { direction } : {}), ...(status ? { status } : {}) } }
  );
  return { emails: Array.isArray(data?.emails) ? data.emails : [] };
}

export async function deleteTenantInboxEmail(tenantId, email) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/inbox/${encodeURIComponent(email.id)}?clientId=${encodeURIComponent(email.clientId || "")}&sourceCollection=${encodeURIComponent(email.sourceCollection || "")}`,
    { method: "DELETE" }
  );
}

export async function deleteClientInboxEmail(tenantId, clientId, email) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/inbox/${encodeURIComponent(email.id)}?sourceCollection=${encodeURIComponent(email.sourceCollection || "")}`,
    { method: "DELETE" }
  );
}

export async function hardDeleteClientInboxEmail(tenantId, clientId, email) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/inbox/${encodeURIComponent(email.id)}?sourceCollection=${encodeURIComponent("deletedEmails")}&action=hard`,
    { method: "DELETE" }
  );
}

export async function sendClientEmail(tenantId, clientId, payload) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/send-email`,
    { method: "POST", body: payload }
  );
}

export async function triggerEmailSync(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/email-sync`, { method: "POST", body: {} });
}

export async function getAttachmentUrl(tenantId, clientId, emailId, index, action = "preview") {
  // Unassigned emails are stored at tenant level and have no clientId, so they
  // are served by a dedicated route.
  if (!clientId) {
    return request(
      `/tenant/${encodeURIComponent(tenantId)}/unassigned-emails/${encodeURIComponent(emailId)}/attachment/${index}?action=${action}`,
      { method: "GET" }
    );
  }
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/inbox/${encodeURIComponent(emailId)}/attachment/${index}?action=${action}`,
    { method: "GET" }
  );
}

/*Bearer ${token}`
 * Maps an unassigned inbound email to a client profile.
 *
 * @param {string} tenantId
 * @param {string} emailId
 * @param {string} clientId Target client profile.
 * @param {boolean} [rememberSender] Also save the sender as the client's contact
 *   email so future messages from them are matched automatically.
 */
export async function assignUnassignedEmail(tenantId, emailId, clientId, rememberSender = false) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/unassigned-emails/${encodeURIComponent(emailId)}/assign`,
    { method: "POST", body: { clientId, rememberSender } }
  );
}

export async function createOrg(payload) {
  return request("/orgs", { method: "POST", body: payload });
}

export async function listOrgs() {
  const data = await request("/orgs", { method: "GET" });
  return { orgs: Array.isArray(data?.orgs) ? data.orgs : [] };
}

export async function updateOrg(orgId, payload) {
  return request(`/orgs/${encodeURIComponent(orgId)}`, { method: "PATCH", body: payload });
}

export async function deleteOrg(orgId) {
  return request(`/orgs/${encodeURIComponent(orgId)}`, { method: "DELETE" });
}

export async function createTenantUser(payload) {
  return request("/tenant/users", {
    method: "POST",
    body: { ...payload, inviteMode: false },
  });
}

export async function createTenantUserInvite(payload) {
  return request("/tenant/users", {
    method: "POST",
    body: { ...payload, inviteMode: true },
  });
}

export async function listTenantUsers(payload) {
  const data = await request("/tenant/users", {
    method: "GET",
    query: payload,
  });
  return { users: Array.isArray(data?.users) ? data.users : [] };
}

export async function setTenantUserStatus(payload) {
  return request(`/tenant/users/${payload.uid}/status`, {
    method: "PATCH",
    body: {
      tenantId: payload.tenantId,
      active: payload.active,
    },
  });
}



export async function createTenantRole(payload) {
  return request("/tenant/roles", {
    method: "POST",
    body: payload,
  });
}

export async function listTenantRoles(payload) {
  const data = await request("/tenant/roles", {
    method: "GET",
    query: payload,
  });

  return {
    roles: Array.isArray(data?.roles) ? data.roles : [],
    availablePermissions: Array.isArray(data?.availablePermissions) ? data.availablePermissions : [],
  };
}

export async function deleteTenantUser({ uid, tenantId }) {
  return request(`/tenant/users/${uid}?tenantId=${encodeURIComponent(tenantId)}`, {
    method: "DELETE",
  });
}

export async function assignTenantUserRoles(payload) {
  return request(`/tenant/users/${payload.uid}/roles`, {
    method: "POST",
    body: {
      tenantId: payload.tenantId,
      roleIds: payload.roleIds,
    },
  });
}

export async function getEmailConfig(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/email-config`);
}

export async function saveEmailConfig(tenantId, fields) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/email-config`, {
    method: "PUT",
    body: fields,
  });
}

export async function getAutoRRStatus(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/auto-rr-status`);
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

export async function listTasks(tenantId, query = {}) {
  const data = await request(`/tenant/${encodeURIComponent(tenantId)}/tasks`, { query });
  return { tasks: Array.isArray(data?.tasks) ? data.tasks : [] };
}

export async function generateTasks(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/tasks/generate`, { method: "POST" });
}

export async function updateTask(tenantId, taskId, updates) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/tasks/${encodeURIComponent(taskId)}`,
    { method: "PATCH", body: updates }
  );
}

export async function deleteTask(tenantId, taskId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/tasks/${encodeURIComponent(taskId)}`,
    { method: "DELETE" }
  );
}

// ── CT600 ─────────────────────────────────────────────────────────────────────

export async function generateCT600(tenantId, clientId, save = false) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/ct600/generate`,
    { method: "POST", body: { save } }
  );
}

export async function listCT600Drafts(tenantId, clientId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/ct600/drafts`
  );
  return { drafts: Array.isArray(data?.drafts) ? data.drafts : [] };
}

// ── Confirmation Statements ───────────────────────────────────────────────────

export async function listConfirmationStatements(tenantId, clientId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/confirmation-statements`
  );
  return { confirmationStatements: Array.isArray(data?.confirmationStatements) ? data.confirmationStatements : [] };
}

// VAT workflows for a client (used to list periods/drafts)
export async function listVatWorkflows(tenantId, clientId) {
  const data = await request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat/workflows`
  );
  return { workflows: Array.isArray(data?.workflows) ? data.workflows : [], documents: Array.isArray(data?.documents) ? data.documents : [] };
}

export async function createConfirmationStatement(tenantId, clientId, { madeUpTo, notes } = {}) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/confirmation-statements`,
    { method: "POST", body: { madeUpTo, notes } }
  );
}

export async function fileConfirmationStatement(tenantId, clientId, csId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/confirmation-statements/${encodeURIComponent(csId)}/file`,
    { method: "POST" }
  );
}

export async function getConfirmationStatement(tenantId, clientId, csId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/confirmation-statements/${encodeURIComponent(csId)}`
  );
}

export async function pollConfirmationStatementStatus(tenantId, clientId, csId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/confirmation-statements/${encodeURIComponent(csId)}/poll-status`,
    { method: "POST" }
  );
}

// Helper URLs for documents: authenticated download and public capability link
export function getDocumentDownloadUrl(tenantId, clientId, docId) {
  return buildUrl(`/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(docId)}/download`);
}

export function getPublicDocumentUrl(token) {
  return buildUrl(`/public/doc/${encodeURIComponent(token)}`);
}

// Regenerate a public capability token for a document (returns { token, publicUrl, expiresAt })
export async function regenerateDocumentPublicToken(tenantId, clientId, docId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(docId)}/public-token`,
    { method: "POST" }
  );
}

// Revoke any public token(s) for a document
export async function revokeDocumentPublicToken(tenantId, clientId, docId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(docId)}/public-token`,
    { method: "DELETE" }
  );
}

// ── AD01 (Address Change) ─────────────────────────────────────────────────────

export async function listAd01Filings(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/ad01-filings`
  );
}

export async function createAd01Filing(tenantId, clientId, { newAddress, notes } = {}) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/ad01-filings`,
    { method: "POST", body: { newAddress, notes } }
  );
}

export async function pollAd01Status(tenantId, clientId, ad01Id) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/ad01-filings/${encodeURIComponent(ad01Id)}/poll`,
    { method: "POST" }
  );
}

// ── Email Templates ───────────────────────────────────────────────────────────

export async function getEmailTemplates(tenantId) {
  const data = await request(`/tenant/${encodeURIComponent(tenantId)}/email-templates`);
  return data?.templates || {};
}

export async function saveEmailTemplate(tenantId, key, { subject, body }) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/email-templates/${encodeURIComponent(key)}`,
    { method: "PUT", body: { subject, body } }
  );
}

export async function sendCsReview(tenantId, clientId, { madeUpTo, agentNotes } = {}) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/cs-reviews`,
    {
      method: "POST",
      body: { madeUpTo, agentNotes },
      timeoutMs: 60_000,
      timeoutMessage: "The customer-review email is taking too long to send. Check the outbound-email settings, then try again.",
    }
  );
}

export async function getLatestCsReview(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/cs-reviews/latest`
  );
}

export async function deleteConfirmationStatement(tenantId, clientId, csId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/confirmation-statements/${encodeURIComponent(csId)}`,
    { method: "DELETE" }
  );
}

export async function clearPendingCsReviews(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/cs-reviews/pending`,
    { method: "DELETE" }
  );
}

export async function agentApproveReview(tenantId, clientId, reviewId, { notes } = {}) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/cs-reviews/${encodeURIComponent(reviewId)}/agent-approve`,
    { method: "POST", body: { notes } }
  );
}

// ── Fee Schedule ──────────────────────────────────────────────────────────────

export async function getFeeSchedule(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/fee-schedule`);
}

export async function updateFeeSchedule(tenantId, fees) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/fee-schedule`, {
    method: "PUT",
    body: { fees },
  });
}

// ── Charges ───────────────────────────────────────────────────────────────────

export async function listCharges(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/charges`
  );
}

export async function generateCharges(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/charges/generate`,
    { method: "POST" }
  );
}

export async function updateChargeStatus(tenantId, clientId, chargeId, status) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/charges/${encodeURIComponent(chargeId)}/status`,
    { method: "PATCH", body: { status } }
  );
}

// ── Client Secrets ────────────────────────────────────────────────────────────

export async function getClientSecrets(tenantId, clientId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/secrets`
  );
}

export async function saveClientSecrets(tenantId, clientId, fields) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/secrets`,
    { method: "PUT", body: fields }
  );
}

export async function getFilingDocumentUrl(docUrl) {
  const token = await getBearerToken();
  const resp = await fetch(buildUrl("/companies-house/document", { docUrl }), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: "Document unavailable" }));
    throw new Error(err.error || "Failed to fetch document");
  }

  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

// ── AI Chat ───────────────────────────────────────────────────────────────────

/*Bearer ${token}`
 * Send a chat message to the tenant-scoped AI assistant.
 * @param {string} tenantId
 * @param {{ messages: Array<{role:string,content:string}>, pageContext?: object }} payload
 */
export async function sendAiChatMessage(tenantId, { messages, pageContext } = {}) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/ai-chat`, {
    method: "POST",
    body: { messages, pageContext },
  });
}

// ── Client Documents ───────────────────────────────────────────────────────────

export async function listClientDocuments(tenantId, clientId) {
  try {
    return await request(
      `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/documents`
    );
  } catch (err) {
    // Backend endpoint not yet implemented — treat as empty
    if (/404|not found/i.test(err?.message || "")) return { documents: [] };
    throw err;
  }
}

export async function uploadClientDocument(tenantId, clientId, file, category) {
  const token = await getBearerToken();
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);
  const response = await fetch(
    `${process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1"}/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/documents`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData }
  );
  if (response.status === 404) throw new Error("Document upload is not yet available — please check the backend.");
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Upload failed with status ${response.status}`);
  return payload;
}

export async function deleteClientDocument(tenantId, clientId, docId) {
  return request(
    `/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(docId)}`,
    { method: "DELETE" }
  );
}

export async function backupTenant(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/backup`);
}

export async function restoreTenant(tenantId, backupData) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/restore`, {
    method: "POST",
    body: backupData,
  });
}

// ── SSO Settings ──────────────────────────────────────────────────────────────

export async function getSSOSettings(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/sso`);
}

export async function saveSSOSettings(tenantId, settings) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/sso`, {
    method: "PUT",
    body: settings,
  });
}

export async function setUserSSOEnabled(tenantId, uid, ssoEnabled) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(uid)}/sso`, {
    method: "PATCH",
    body: { ssoEnabled },
  });
}

export async function updateTenantUser(tenantId, uid, payload) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(uid)}`, {
    method: "PATCH",
    body: payload,
  });
}

// ── SSO Allowed Emails ───────────────────────────────────────────────────────

export async function listSSOAllowedEmails(tenantId) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/sso/allowed-emails`);
}

export async function addSSOAllowedEmail(tenantId, { email, role, displayName }) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/sso/allowed-emails`, {
    method: "POST",
    body: { email, role, displayName },
  });
}

export async function removeSSOAllowedEmail(tenantId, email) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/sso/allowed-emails/${encodeURIComponent(email)}`, {
    method: "DELETE",
  });
}

// Record a VAT correction (direct fetch to ensure Authorization header set correctly)
export async function recordVatCorrection(tenantId, clientId, periodId, correction) {
  const token = await getBearerToken();
  const url = `${process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1"}/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(periodId)}/corrections`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ correction }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.error || `Request failed with status ${resp.status}`);
  return payload;
}

export async function listVatCorrections(tenantId, clientId, periodId, { approvedOnly = false, limit = 200 } = {}) {
  const token = await getBearerToken();
  const query = [];
  if (approvedOnly) query.push('approvedOnly=true');
  if (limit) query.push(`limit=${encodeURIComponent(limit)}`);
  const q = query.length ? `?${query.join('&')}` : '';
  const url = `${process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1"}/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(periodId)}/corrections${q}`;
  const resp = await fetch(url, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.error || `Request failed with status ${resp.status}`);
  return payload.corrections || payload || [];
}

export async function approveVatCorrection(tenantId, clientId, periodId, correctionId) {
  const token = await getBearerToken();
  const url = `${process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1"}/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(periodId)}/corrections/${encodeURIComponent(correctionId)}/approve`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.error || `Request failed with status ${resp.status}`);
  return payload;
}

export async function updateSSOAllowedEmailRole(tenantId, email, role) {
  return request(`/tenant/${encodeURIComponent(tenantId)}/settings/sso/allowed-emails/${encodeURIComponent(email)}`, {
    method: "PATCH",
    body: { role },
  });
}

// ── Dashboard & reporting helpers (used by dashboard widgets) ─────────────

export async function getBankBalances(tenantId) {
  try {
    const data = await request(`/tenant/${encodeURIComponent(tenantId)}/dashboard/bank-balances`);
    return { balances: Array.isArray(data?.balances) ? data.balances : [] };
  } catch (err) {
    if (/404|not found/i.test(err?.message || "")) return { balances: [] };
    throw err;
  }
}

export async function getAgedReceivables(tenantId) {
  try {
    const data = await request(`/tenant/${encodeURIComponent(tenantId)}/dashboard/aged-receivables`);
    return { buckets: Array.isArray(data?.buckets) ? data.buckets : [] };
  } catch (err) {
    if (/404|not found/i.test(err?.message || "")) return { buckets: [] };
    throw err;
  }
}

export async function getVatDeadlines(tenantId) {
  try {
    const data = await request(`/tenant/${encodeURIComponent(tenantId)}/dashboard/vat-deadlines`);
    return { deadlines: Array.isArray(data?.deadlines) ? data.deadlines : [] };
  } catch (err) {
    if (/404|not found/i.test(err?.message || "")) return { deadlines: [] };
    throw err;
  }
}

export async function getCashflowForecast(tenantId) {
  try {
    const data = await request(`/tenant/${encodeURIComponent(tenantId)}/dashboard/cashflow-forecast`);
    return { points: Array.isArray(data?.points) ? data.points : [] };
  } catch (err) {
    if (/404|not found/i.test(err?.message || "")) return { points: [] };
    throw err;
  }
}

export async function getClientHealth(tenantId) {
  try {
    const data = await request(`/tenant/${encodeURIComponent(tenantId)}/dashboard/client-health`);
    return { scores: Array.isArray(data?.scores) ? data.scores : [] };
  } catch (err) {
    if (/404|not found/i.test(err?.message || "")) return { scores: [] };
    throw err;
  }
}

export async function getRevenueTrends(tenantId) {
  try {
    const data = await request(`/tenant/${encodeURIComponent(tenantId)}/dashboard/revenue-trends`);
    return { points: Array.isArray(data?.points) ? data.points : [] };
  } catch (err) {
    if (/404|not found/i.test(err?.message || "")) return { points: [] };
    throw err;
  }
}

export async function getSystemHealth(tenantId) {
  try {
    const data = await request(`/system/health`);
    return data || { reachable: true };
  } catch (err) {
    return { reachable: false };
  }
}

// ── Google SSO Login (public, no auth token needed) ──────────────────────────

export async function googleSSOLogin(idToken) {
  const API_BASE = process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";
  const res = await fetch(`${API_BASE}/sso/google-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "SSO login failed");
  return data;
}


























